import type { ReactElement } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Package,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { listImagePackTasks } from '@/services/imagePackService'
import {
  IMAGE_PACK_REF_TYPE,
  IMAGE_PACK_STATUS,
  IMAGE_PACK_STATUS_MAP,
} from '@/types/imagePack'
import type {
  ImagePackRefType,
  ImagePackStatus,
  ImagePackTask,
} from '@/types/imagePack'

const PAGE_SIZE = 20
const POLLING_INTERVAL = 5000

type StatusFilter = 'all' | ImagePackStatus

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: IMAGE_PACK_STATUS.PENDING, label: '排队中' },
  { value: IMAGE_PACK_STATUS.PROCESSING, label: '打包中' },
  { value: IMAGE_PACK_STATUS.COMPLETED, label: '已完成' },
  { value: IMAGE_PACK_STATUS.FAILED, label: '失败' },
]

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getStatusDotColor(status: ImagePackStatus): string {
  switch (status) {
    case IMAGE_PACK_STATUS.PENDING:
      return 'bg-amber-500'
    case IMAGE_PACK_STATUS.PROCESSING:
      return 'bg-blue-500'
    case IMAGE_PACK_STATUS.COMPLETED:
      return 'bg-emerald-500'
    case IMAGE_PACK_STATUS.FAILED:
      return 'bg-red-500'
    default:
      return 'bg-muted-foreground'
  }
}

export function ImagePackCenterPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()

  const refId = searchParams.get('ref_id') ?? ''
  const refTypeQuery = searchParams.get('ref_type')
  const refType =
    refTypeQuery !== null && Object.values(IMAGE_PACK_REF_TYPE).includes(refTypeQuery as ImagePackRefType)
      ? (refTypeQuery as ImagePackRefType)
      : refId !== ''
        ? IMAGE_PACK_REF_TYPE.BATCH_TASK
        : undefined
  const statusQuery = searchParams.get('status')
  const initialFilter =
    statusQuery !== null &&
    STATUS_FILTER_OPTIONS.some((item) => item.value === statusQuery)
      ? (statusQuery as StatusFilter)
      : 'all'

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialFilter)
  const [items, setItems] = useState<ImagePackTask[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)

  const totalPages = useMemo((): number => {
    return Math.max(1, Math.ceil(total / PAGE_SIZE))
  }, [total])

  const hasActiveTask = useMemo((): boolean => {
    return items.some((item) => item.status === IMAGE_PACK_STATUS.PENDING || item.status === IMAGE_PACK_STATUS.PROCESSING)
  }, [items])

  const fetchTasks = useCallback(async (showLoading = true): Promise<void> => {
    if (showLoading) {
      setIsLoading(true)
    }

    try {
      const response = await listImagePackTasks({
        page,
        page_size: PAGE_SIZE,
        ...(refId !== ''
          ? {
              ...(refType !== undefined ? { ref_type: refType } : {}),
              ref_id: refId,
            }
          : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      })

      setItems(response.items)
      setTotal(response.total)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取下载队列失败')
    } finally {
      setIsLoading(false)
    }
  }, [page, refId, refType, statusFilter])

  useEffect(() => {
    void fetchTasks(true)
  }, [fetchTasks])

  useEffect(() => {
    if (!hasActiveTask) {
      return
    }

    const timer = window.setTimeout((): void => {
      void fetchTasks(false)
    }, POLLING_INTERVAL)

    return (): void => {
      window.clearTimeout(timer)
    }
  }, [hasActiveTask, fetchTasks])

  const handleStatusFilterChange = useCallback((value: StatusFilter): void => {
    setStatusFilter(value)
    setPage(1)

    const next = new URLSearchParams(searchParams)
    if (value === 'all') {
      next.delete('status')
    } else {
      next.set('status', value)
    }
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleDownload = useCallback((task: ImagePackTask): void => {
    if (task.zip_url === null || task.zip_url === '') {
      return
    }

    const link = document.createElement('a')
    link.href = task.zip_url
    const filename = task.zip_filename !== undefined && task.zip_filename !== ''
      ? task.zip_filename
      : task.ref_id.slice(0, 8)
    link.download = `${filename}.zip`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Archive className="h-7 w-7 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">下载中心</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            查看 ZIP 打包队列，任务完成后可直接下载 OSS 文件。
          </p>
          {refId !== '' && (
            <p className="text-xs text-muted-foreground">
              当前仅展示
              <span className="font-medium">
                {refType === IMAGE_PACK_REF_TYPE.CRAWL_BATCH ? ' 采集批次 ' : ' 批量任务 '}
                {refId.slice(0, 8)}
              </span>
              的打包记录
            </p>
          )}
        </div>

        <Button asChild variant="outline">
          <Link to={refType === IMAGE_PACK_REF_TYPE.CRAWL_BATCH && refId !== '' ? `/materials/${refId}` : '/records/generations'}>
            <ChevronLeft className="h-4 w-4" />
            {refType === IMAGE_PACK_REF_TYPE.CRAWL_BATCH ? '返回素材包详情' : '返回生图记录'}
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        {STATUS_FILTER_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={statusFilter === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={(): void => {
              handleStatusFilterChange(option.value)
            }}
          >
            {option.label}
          </Button>
        ))}

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={(): void => {
            void fetchTasks(true)
          }}
        >
          刷新
        </Button>
      </div>

      {error !== null && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="mr-2 inline h-4 w-4" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((id) => (
            <div key={id} className="h-28 animate-pulse rounded-lg border bg-card" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border bg-card py-16 text-center">
          <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-muted-foreground">暂无打包任务</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const statusMeta = IMAGE_PACK_STATUS_MAP[item.status]
            return (
              <div key={item.id} className="space-y-3 rounded-xl border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">任务 {item.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      创建于 {formatDateTime(item.created_at)}
                    </p>
                    {item.ref_type === IMAGE_PACK_REF_TYPE.BATCH_TASK && (
                      <Link
                        to={`/records/generations/${item.ref_id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        查看来源批量任务
                      </Link>
                    )}
                    {item.ref_type === IMAGE_PACK_REF_TYPE.CRAWL_BATCH && (
                      <Link
                        to={`/materials/${item.ref_id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        查看来源素材包
                      </Link>
                    )}
                  </div>

                  <div
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
                      statusMeta.color
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', getStatusDotColor(item.status))} />
                    {statusMeta.text}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>进度 {item.processed_count}/{item.total_count}</span>
                    <span>{item.progress}%</span>
                  </div>
                  <Progress value={item.progress} className="h-2" />
                </div>

                {item.status === IMAGE_PACK_STATUS.FAILED && item.error_message !== null && (
                  <div className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                    {item.error_message}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {item.status === IMAGE_PACK_STATUS.COMPLETED && item.zip_url !== null && (
                    <Button
                      size="sm"
                      onClick={(): void => {
                        handleDownload(item)
                      }}
                    >
                      <Download className="h-4 w-4" />
                      下载 ZIP
                    </Button>
                  )}

                  {item.status === IMAGE_PACK_STATUS.COMPLETED && item.zip_url === null && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      打包已完成，等待下载地址刷新
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
        <p className="text-sm text-muted-foreground">
          共 {total} 条记录，第 {page}/{totalPages} 页
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page <= 1 || isLoading}
            onClick={(): void => {
              setPage((prev) => Math.max(1, prev - 1))
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page >= totalPages || isLoading}
            onClick={(): void => {
              setPage((prev) => Math.min(totalPages, prev + 1))
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
